<template>
  <div class="flex flex-col gap-4 p-5 h-full g-body-width mx-auto">
    <TransitionGroup
      name="slide-up"
      tag="div"
      class="flex flex-col gap-4 overflow-y-auto"
      appear
    >
      <div
        v-for="(item, idx) in cards"
        :key="idx"
        class="flex flex-col gap-3 bg-white rounded-lg shadow-sm px-4 py-3"
        :style="{ '--delay': `${idx * 0.2}s` }"
      >
        <div class="flex gap-2 items-center justify-between w-full">
          <div
            class="flex-1 overflow-hidden cursor-pointer"
            @click="toggleCard(idx)"
          >
            <CardEssence
              :card="item.card"
              :single-line="!expandedCards[idx]"
            />
          </div>
          <span
            v-if="showNumbers[idx] && masteryGrowth[idx] > 0"
            class="mastery-growth"
            :class="{
              'text-ld-premium-500!': isPremium[idx],
              'has-glow-effect': isPremium[idx],
            }"
          >
            +{{ masteryGrowth[idx] }}%
          </span>
        </div>
        <ProgressBar
          :percent="progressValues[idx]"
          :color="isPremium[idx] ? premiumGradient : undefined"
          class="h-8px!"
          :class="{ 'glow-effect': showGlowEffects[idx] }"
        />
      </div>
    </TransitionGroup>
    <Button
      class="w-full mt-auto"
      scene="primary"
      :label="_t('common.continue')"
      @click="$emit('continue')"
    ></Button>
  </div>
</template>

<script setup lang="ts">
import { type Card } from '@/types/core'
import CardEssence from './CardEssence/CardEssence.vue'
import ProgressBar from './ProgressBar.vue'
import Button from './Button.vue'
import { ref, computed, onMounted } from 'vue'

const props = defineProps<{
  cards: {
    card: Card
    mastery: number
    oldMastery?: number
  }[]
}>()

// 用于存储各卡片的进度值和状态
const progressValues = ref<number[]>([]) // 进度条数值
const showNumbers = ref<boolean[]>([]) // 控制数字显示
const expandedCards = ref<boolean[]>([]) // 控制卡片展开状态
const showGlowEffects = ref<boolean[]>([]) // 控制发光效果显示

// 计算每张卡片的掌握度增长值
const masteryGrowth = computed(() => {
  return props.cards.map(item =>
    Number((item.mastery - (item.oldMastery || 0)).toFixed(2))
  )
})

// Premium样式的渐变色定义（金色）
const premiumGradient =
  'linear-gradient(90.73deg, var(--ld-premium-300) -0.17%, var(--ld-premium-400) 99.85%)'

// 跟踪哪些卡片达到了满级Premium状态
const isPremium = ref<boolean[]>(
  props.cards.map(item => item.oldMastery == 100)
)

// 切换卡片展开/折叠状态
const toggleCard = (index: number) => {
  expandedCards.value[index] = !expandedCards.value[index]
}

onMounted(() => {
  // 初始化所有状态数组
  progressValues.value = props.cards.map(item => item.oldMastery ?? 0)
  showNumbers.value = props.cards.map(() => false)
  expandedCards.value = props.cards.map(() => false)
  showGlowEffects.value = props.cards.map(() => false)

  // 依次触发各卡片的动画效果
  props.cards.forEach((item, idx) => {
    setTimeout(() => {
      // 更新进度条值，触发进度条动画
      progressValues.value[idx] = item.mastery

      // 在进度条动画完成后显示数字与特效
      setTimeout(() => {
        showNumbers.value[idx] = true

        // 如果达到100%，激活Premium特效
        if (item.mastery == 100) {
          isPremium.value[idx] = true

          // 触发进度条发光效果
          showGlowEffects.value[idx] = true

          // 发光效果结束后移除类名，但保持金色
          setTimeout(() => {
            showGlowEffects.value[idx] = false
          }, 1000) // 发光效果持续时间，与动画时长保持一致
        }
      }, 200) // 等待进度条动画完成的时间
    }, idx * 200) // 卡片间的延迟时间
  })
})

defineEmits<{
  (e: 'continue'): void
}>()
</script>

<style scoped>
/* 卡片切换动画效果 */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.5s ease;
  transition-delay: var(--delay, 0s);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(30px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.slide-up-move {
  transition: transform 0.5s ease;
}

/* 掌握度增长数字的弹出效果 */
.mastery-growth {
  animation: pop-in 0.5s cubic-bezier(0.7, 1.5, 1, 1);
  color: var(--ld-brand-500);
  font-weight: 600;
  font-family: 'Comic Sans MS', cursive;
  transform: rotate(-5deg) translateY(8px);
  display: inline-block;
}

/* 同时应用弹出效果和发光效果的类 */
.has-glow-effect {
  animation:
    pop-in 0.5s cubic-bezier(0.7, 1.5, 1, 1),
    number-glow 1s ease-in-out forwards;
}

@keyframes pop-in {
  0% {
    transform: scale(0) rotate(-5deg) translateY(8px);
  }
  100% {
    transform: scale(1) rotate(-5deg) translateY(8px);
  }
}

/* 进度条达到100%时的发光效果 */
.glow-effect {
  animation: progress-glow 1s ease-in-out forwards;
}

/* 进度条发光动画关键帧 */
@keyframes progress-glow {
  0% {
    filter: brightness(1) drop-shadow(0 0 0px var(--ld-premium-300));
  }
  20% {
    filter: brightness(1.05) drop-shadow(0 0 5px var(--ld-premium-300));
  }
  50% {
    filter: brightness(1.1) drop-shadow(0 0 8px var(--ld-premium-300));
  }
  80% {
    filter: brightness(1.05) drop-shadow(0 0 4px var(--ld-premium-300));
  }
  100% {
    filter: brightness(1) drop-shadow(0 0 0px var(--ld-premium-300));
  }
}

/* 数字发光动画关键帧 - 与进度条保持一致的动画效果 */
@keyframes number-glow {
  0% {
    filter: brightness(1) drop-shadow(0 0 0px var(--ld-premium-300));
  }
  20% {
    filter: brightness(1.05) drop-shadow(0 0 2px var(--ld-premium-300));
  }
  50% {
    filter: brightness(1.1) drop-shadow(0 0 4px var(--ld-premium-300));
  }
  80% {
    filter: brightness(1.05) drop-shadow(0 0 2px var(--ld-premium-300));
  }
  100% {
    filter: brightness(1) drop-shadow(0 0 0px var(--ld-premium-300));
  }
}
</style>
